import type { CrdtHistoryDelta, CrdtHistoryResponse } from "@/lib/compiler/types"

const MAX_DELTAS_PER_SESSION = 200
const PLACEHOLDER_BASE64 = /^A+={0,2}$/

export type ClientCapturedDelta = {
  version: number
  path: string
  at: string
  update: string
}

const buffers = new Map<string, ClientCapturedDelta[]>()

function encodeUpdateBase64(bytes: Uint8Array): string {
  let binary = ""
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function isTrivialUpdate(bytes: Uint8Array): boolean {
  return bytes.byteLength === 0 || bytes.every((byte) => byte === 0)
}

export function isPlaceholderHistoryUpdate(updateBase64: string): boolean {
  return PLACEHOLDER_BASE64.test(updateBase64.trim())
}

/** Record a local sync_update payload (raw Yjs bytes) for history replay fallback. */
export function recordClientCrdtDelta(
  sessionId: string,
  path: string,
  update: Uint8Array,
): void {
  const normalizedPath = path.trim()
  if (!sessionId || !normalizedPath || isTrivialUpdate(update)) return

  const list = buffers.get(sessionId) ?? []
  const nextVersion = list.length > 0 ? list[list.length - 1].version + 1 : 1
  list.push({
    version: nextVersion,
    path: normalizedPath,
    at: new Date().toISOString(),
    update: encodeUpdateBase64(update),
  })

  buffers.set(
    sessionId,
    list.length > MAX_DELTAS_PER_SESSION ? list.slice(-MAX_DELTAS_PER_SESSION) : list,
  )
}

export function getClientCrdtHistoryDeltas(sessionId: string): ClientCapturedDelta[] {
  return [...(buffers.get(sessionId) ?? [])]
}

export function clearClientCrdtHistory(sessionId: string): void {
  buffers.delete(sessionId)
}

export function apiHistoryIsAllPlaceholders(deltas: CrdtHistoryDelta[]): boolean {
  if (deltas.length === 0) return false
  return deltas.every((delta) => isPlaceholderHistoryUpdate(delta.update))
}

/**
 * When the backend returns only AAA= placeholders, replay from locally captured WS payloads.
 */
export function mergeApiHistoryWithClientBuffer(
  api: CrdtHistoryResponse,
): CrdtHistoryResponse & { usedClientFallback: boolean } {
  if (!apiHistoryIsAllPlaceholders(api.deltas)) {
    return { ...api, usedClientFallback: false }
  }

  const clientDeltas = getClientCrdtHistoryDeltas(api.session_id)
  if (clientDeltas.length === 0) {
    return { ...api, usedClientFallback: false }
  }

  const deltas: CrdtHistoryDelta[] = clientDeltas.map((entry) => ({
    version: entry.version,
    session_id: api.session_id,
    path: entry.path,
    at: entry.at,
    update: entry.update,
  }))

  return {
    session_id: api.session_id,
    latest_version: deltas[deltas.length - 1]?.version ?? api.latest_version,
    deltas,
    usedClientFallback: true,
  }
}
